import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import Cookies from "js-cookie"
const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const socketRef = useRef(null);
  const listenersRef = useRef({}); // { event: Set<handler> }
  const [connected, setConnected] = useState(false);

  // Doctor tokens are stored in a plain (JS-readable) cookie, so they can be
  // passed explicitly. The patient accessToken cookie is httpOnly by design
  // (invisible to document.cookie) — for patients we rely on withCredentials
  // sending it automatically, with the backend gateway falling back to
  // reading it straight off the raw Cookie header.
  const token =
    Cookies.get("doctorAccessToken") ||
    Cookies.get("doctor_token") ||
    Cookies.get("doctorToken") ||
    Cookies.get("accessToken") ||
    Cookies.get("auth_token") ||
    Cookies.get("token");

  useEffect(() => {
    // Don't gate the connection attempt on a JS-readable token — a logged-in
    // patient may only have an httpOnly cookie, which withCredentials still
    // sends to the server. An anonymous visitor simply won't have either.
   // Resolve socket URL and adapt protocol to the current page to avoid
   // mixed-content / CORS issues when developing with HTTPS.
   let configured =
     import.meta.env.VITE_SOCKET_URL ||
     import.meta.env.VITE_GATEWAY_URL ||
     window.location.origin;
   try {
     if (configured.startsWith("http:")) {
       configured = configured.replace(/^http:/, window.location.protocol);
     }
   } catch (e) {
     // ignore in SSR/tests
   }

   const socketBase = configured
     .replace(/\/api\/?$/i, "")
     .replace(/\/+$/, "");
   const socketEndpoint = /\/ws\/messages$/i.test(socketBase)
     ? socketBase
     : `${socketBase}/ws/messages`;

   const socket = io(socketEndpoint, {
     path: import.meta.env.VITE_SOCKET_PATH || "/socket.io",
     auth: token ? { token } : undefined,
     withCredentials: true,
     transports: ["websocket", "polling"],
     autoConnect: true,
   });

    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      console.info("[MESSAGES-WS] connected", { id: socket.id, url: socketEndpoint });
    });
    socket.on("connect_error", (error) => {
      console.error("[MESSAGES-WS] connect_error", error?.message || String(error));
    });
    socket.io.on("reconnect", (attempt) => {
      console.info("[MESSAGES-WS] reconnect", { attempt, id: socket.id });
    });
    socket.on("disconnect", (reason) => {
      setConnected(false);
      console.warn("[MESSAGES-WS] disconnect", { reason });
    });

    // ✅ forward all events to registered listeners
    const EVENTS = ["message:new", "message:typing", "message:error", "connection_confirmed", "newMessage", "readMessage"];
    EVENTS.forEach((ev) => {
      socket.on(ev, (data) => {
        const handlers = listenersRef.current[ev];
        if (handlers) handlers.forEach((fn) => fn(data));
      });
    });

    return () => {
      socket.disconnect();
      socket.io.off("reconnect");
      socketRef.current = null;
      setConnected(false);
    };
  }, [token]); // reconnects if token changes (re-login)

  const emit = useCallback((event, data) => {
    socketRef.current?.emit(event, data);
  }, []);

  // ✅ returns unsubscribe fn
  const subscribe = useCallback((event, handler) => {
    if (!listenersRef.current[event]) listenersRef.current[event] = new Set();
    listenersRef.current[event].add(handler);
    return () => listenersRef.current[event]?.delete(handler);
  }, []);

  return (
    <SocketContext.Provider value={{ connected, emit, subscribe }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);